import { useCallback, useEffect, useMemo, useState } from 'react';

// Chromium fires `beforeinstallprompt` with these extra members.
// MDN: https://developer.mozilla.org/en-US/docs/Web/API/BeforeInstallPromptEvent
export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

const DISMISS_KEY = 'newswave.installDismissedAt';
const DISMISS_DAYS = 30;

export function isAndroidUA(ua: string): boolean {
  return /android/i.test(ua);
}

export function wasDismissedRecently(
  now: number,
  storedTs: number | null,
  windowDays: number = DISMISS_DAYS,
): boolean {
  if (!storedTs) return false;
  return now - storedTs < windowDays * 24 * 3600 * 1000;
}

function readDismissedAt(): number | null {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function writeDismissedAt(ts: number): void {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(ts));
  } catch {
    // localStorage may be disabled in private modes — ignore.
  }
}

export interface UseInstallPromptResult {
  canInstall: boolean;
  promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
  dismiss: () => void;
}

export function useInstallPrompt(): UseInstallPromptResult {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const [dismissedAt, setDismissedAt] = useState<number | null>(() =>
    readDismissedAt(),
  );

  useEffect(() => {
    const onPrompt = (e: Event): void => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = (): void => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const canInstall = useMemo(() => {
    if (installed) return false;
    if (!deferred) return false;
    if (typeof navigator === 'undefined') return false;
    if (!isAndroidUA(navigator.userAgent)) return false;
    if (wasDismissedRecently(Date.now(), dismissedAt)) return false;
    return true;
  }, [deferred, installed, dismissedAt]);

  const promptInstall = useCallback(async (): Promise<
    'accepted' | 'dismissed' | 'unavailable'
  > => {
    if (!deferred) return 'unavailable';
    await deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    if (choice.outcome === 'dismissed') {
      const ts = Date.now();
      writeDismissedAt(ts);
      setDismissedAt(ts);
    }
    return choice.outcome;
  }, [deferred]);

  const dismiss = useCallback((): void => {
    const ts = Date.now();
    writeDismissedAt(ts);
    setDismissedAt(ts);
    setDeferred(null);
  }, []);

  return { canInstall, promptInstall, dismiss };
}
